import React, { useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';

import { Box, AppBar, Toolbar, IconButton, Badge } from '@mui/material';
import UserMenu from './components/UserMenu';
import MobileMenu from './components/MobileMenu';
import Logo from '../../assets/logo.png';
import NotificationMenu from './components/NotificationMenu';
import { useAuth } from '../../context/AuthContext';
import DigitalClock from './components/DigitalClock';
import { CLINIC } from '../../clinic';

function AppTopBar() {
    /* VARIABLE DECELERATION
    -------------------------------------------------------------------------------------*/
    const { activeUser, pageTitle, notificationsCount, drawerToggle } =
        useAuth();

    const [userMenuEl, setUserMenuEl] = useState(null);
    const [mdlUserMenuEl, setMdlUserMenuEl] = useState(false);
    const [mobileEl, setMobileEl] = useState(null);
    const [mdlMobileEl, setMdlMobileEl] = useState(false);
    const [notificationEl, setNotificationEl] = useState(null);
    const [mdlNotificationEl, setMdlNotificationEl] = useState(false);

    const navigate = useNavigate();
    /* EVENT LISTENERS
    -------------------------------------------------------------------------------------*/

    /* ASYNC FUNCTIONS
    -------------------------------------------------------------------------------------*/

    /* EVENT FUNCTIONS
    -------------------------------------------------------------------------------------*/
    function gotoDashboard() {
        navigate('/dashboard');
    }

    function handleUserMenuOpen(event) {
        setUserMenuEl(event.currentTarget);
        setMdlUserMenuEl(true);
    }

    function handleUserMenuClose() {
        setUserMenuEl(null);
        setMdlUserMenuEl(false);
    }

    function handleMobileMenuOpen(event) {
        setMobileEl(event.currentTarget);
        setMdlMobileEl(true);
    }

    function handleMobileMenuClose() {
        setMobileEl(null);
        setMdlMobileEl(false);
    }

    function handleNotificationMenuOpen(event) {
        setNotificationEl(event.currentTarget);
        setMdlNotificationEl(true);
    }

    function handleNotificationMenuClose() {
        setNotificationEl(false);
        setMdlNotificationEl(false);
        handleMobileMenuClose();
    }

    /* RENDER APP
    -------------------------------------------------------------------------------------*/
    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar position="fixed" color="primary">
                <Toolbar sx={{ alignItems: 'stretch', px: '0 !important' }}>
                    <IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        sx={{
                            ml: '0',
                            mr: { xs: 0, md: 2 },
                            display: { lg: 'none' },
                        }}
                        onClick={drawerToggle}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box
                        onClick={gotoDashboard}
                        sx={{
                            cursor: 'pointer',
                            height: { xs: '56px', sm: '64px' },
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 1,
                            boxSizing: 'border-box',
                            borderBottom: { lg: '1px solid #000000' },
                            width: { lg: '240px' },
                            backgroundColor: { lg: 'primary.dark' },
                            px: 1,
                            '& > img': {
                                width: { xs: 28, md: 36 },
                                height: { xs: 28, md: 36 },
                                objectFit: 'contain',
                                borderRadius: 1,
                            },
                        }}
                    >
                        <Box component="img" src={Logo} alt="" />
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: 12, sm: 14 },
                                lineHeight: 1.2,
                                display: { xs: 'none', sm: 'inline' },
                            }}
                        >
                            {CLINIC.name}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center',
                            pl: '24px',
                        }}
                    >
                        <Box>
                            <Box sx={{ color: '#fff', fontSize: '20px' }}>
                                {pageTitle}
                            </Box>
                            <Box
                                sx={{
                                    color: 'rgba(255,255, 255, 0.75)',
                                    fontSize: '12px',
                                    display: 'flex',
                                    mt: '2px',
                                    letterSpacing: '0.75px',
                                    '& > div': { mr: '8px' },
                                }}
                            >
                                <Box>{activeUser?.name}</Box>
                                <Box>{activeUser?.email}</Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            flexGrow: 1,
                            display: { xs: 'none', md: 'block' },
                        }}
                    />

                    <Box
                        component="ul"
                        sx={{
                            display: 'flex',
                            listStyle: 'none',
                            p: 0,
                            m: 0,
                            ml: 'auto',
                            '& > li': {
                                borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
                                boxShadow:
                                    'inset 1px 0 rgba(255, 255, 255, 0.12)',
                            },
                        }}
                    >
                        <Box
                            component="li"
                            sx={{ display: { xs: 'none', md: 'block' } }}
                        >
                            <DigitalClock userMenu={false} />
                        </Box>
                        <Box
                            component="li"
                            sx={{ display: { xs: 'none', lg: 'flex' } }}
                        >
                            {activeUser?.role === 'patient' ||
                            activeUser?.role === 'assistant' ? (
                                <IconButton
                                    size="large"
                                    color="inherit"
                                    onClick={handleNotificationMenuOpen}
                                >
                                    <Badge
                                        badgeContent={notificationsCount}
                                        color="error"
                                    >
                                        <NotificationsIcon />
                                    </Badge>
                                </IconButton>
                            ) : null}
                        </Box>
                        <Box
                            component="li"
                            sx={{ display: { xs: 'none', lg: 'flex' } }}
                        >
                            <IconButton
                                onClick={handleUserMenuOpen}
                                color="inherit"
                                size="large"
                            >
                                <AccountCircleIcon />
                            </IconButton>
                        </Box>
                        <Box
                            component="li"
                            sx={{ display: { xs: 'flex', lg: 'none' } }}
                        >
                            <IconButton
                                size="large"
                                onClick={handleMobileMenuOpen}
                                color="inherit"
                            >
                                <MoreVertIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>

            <MobileMenu
                anchorEl={mobileEl}
                handleMenuOpen={mdlMobileEl}
                handleMobileMenuClose={handleMobileMenuClose}
                handleUserMenuOpen={handleUserMenuOpen}
                handleNotificationMenuOpen={handleNotificationMenuOpen}
            />

            <NotificationMenu
                anchorEl={notificationEl}
                handleMenuOpen={mdlNotificationEl}
                handleMenuClose={handleNotificationMenuClose}
            />

            <UserMenu
                anchorEl={userMenuEl}
                handleMenuOpen={mdlUserMenuEl}
                handleMenuClose={handleUserMenuClose}
            />
        </Box>
    );
}

export default AppTopBar;
